import { DatePipe } from '@angular/common';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';

// time.types.ts
export interface TimeComponents {
  start?: string;
  end?: string;
  revisions?: string;
}

export type TimeOverride = 'start' | 'end';

// time-formatter.ts
export class TimeFormatter {
  private static readonly TIME_SEPARATORS = /-|:|\//;
  private static readonly datePipe = new DatePipe('en-US');

  static formatTimeString(
    time: string | number | undefined,
    multiplier = 1,
    format = 'dd/hh:mm',
    locale = 'UTC'
  ): string {
    if (!time) return '';
    return (
      this.datePipe.transform(Number(time) * multiplier, format, locale) ?? ''
    );
  }

  static parseTimeString(time?: string | number): string[] {
    return `${time}`.split(this.TIME_SEPARATORS).filter(isDefined);
  }
}

// twc-time.ts
export class TwcTime {
  readonly start?: string;
  readonly end?: string;
  readonly revisions?: string;
  readonly originalString: string;

  constructor(time?: string | number) {
    this.originalString = `${time}`;
    [this.start, this.end, this.revisions] =
      TimeFormatter.parseTimeString(time);
  }

  toArray(): string[] {
    return TimeFormatter.parseTimeString(this.originalString);
  }

  stringify(
    multiplier = 1,
    format?: string,
    locale = 'UTC',
    override?: TimeOverride
  ): string {
    const components = [this.start, this.end, this.revisions].filter(isDefined);

    if (override === 'start' || components.length === 1) {
      return this.formatSingle(components[0], multiplier, format, locale);
    }

    if (override === 'end' && components.length >= 2) {
      return this.formatSingle(components[1], multiplier, format, locale);
    }

    if (components.length === 2) {
      return this.formatRange(
        components[0],
        components[1],
        multiplier,
        format,
        locale
      );
    }

    if (components.length === 3) {
      return this.formatRangeWithRevision(
        components,
        multiplier,
        format,
        locale
      );
    }

    return this.formatSingle(components[0], multiplier, format, locale);
  }

  private formatSingle(
    time: string,
    multiplier: number,
    format?: string,
    locale?: string
  ): string {
    return format
      ? TimeFormatter.formatTimeString(time, multiplier, format, locale)
      : time;
  }

  private formatRange(
    start: string,
    end: string,
    multiplier: number,
    format?: string,
    locale?: string
  ): string {
    if (!format) return `${start}/${end}`;

    const formattedStart = TimeFormatter.formatTimeString(
      start,
      multiplier,
      format,
      locale
    );
    const formattedEnd = TimeFormatter.formatTimeString(
      end,
      multiplier,
      format,
      locale
    );
    return `${formattedStart} - ${formattedEnd}`;
  }

  private formatRangeWithRevision(
    components: string[],
    multiplier: number,
    format?: string,
    locale?: string
  ): string {
    if (!format) return `${components[0]}-${components[1]}:${components[2]}`;
    return this.formatRange(
      components[0],
      components[1],
      multiplier,
      format,
      locale
    );
  }
}

export const createTimeMap = (
  times: (number | string)[]
): ExtendedMap<TwcTime> => {
  return new ExtendedMap(
    times.sort((a, b) => (a as string).localeCompare(b as string, undefined, {numeric: true}))
    .map((time) => [`${time}`, new TwcTime(time)])
  );
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;
